// src/data/questionnaireHelpers.js
//
// Runtime helpers for the questionnaires/{studyId} Firestore doc shape
// introduced by ticket 05.
//
// The stored shape mirrors what the old src/data/config.js exported:
//
//   {
//     orgId, studyId, version, updatedAt, updatedBy,
//     sections: [
//       {
//         sectionId: string,
//         title: string,
//         description?: string,
//         questions: [
//           { id, type: 'mcq'|'multi_select_mcq'|'long_answer',
//             text, explanation?, options?, visibleIf?: Rule },
//           { id, type: 'group', title, description?, context?,
//             sub_questions: Question[] },
//         ]
//       }
//     ]
//   }

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { isVisible } from './visibility';

const QUESTIONNAIRES = 'questionnaires';

/** Load a questionnaire doc for a given study. Returns null if missing. */
export async function loadQuestionnaire(studyId) {
  if (!studyId) return null;
  const snap = await getDoc(doc(db, QUESTIONNAIRES, studyId));
  return snap.exists() ? snap.data() : null;
}

/** Write a full questionnaire doc, bumping version and timestamps. */
export async function saveQuestionnaire(studyId, next, { updatedBy = null } = {}) {
  if (!studyId) throw new Error('saveQuestionnaire requires studyId');
  const payload = {
    ...next,
    studyId,
    version: (next.version || 0) + 1,
    updatedAt: serverTimestamp(),
    updatedBy,
  };
  await setDoc(doc(db, QUESTIONNAIRES, studyId), payload);
}

/**
 * Walks the nested section → question → (group → sub_questions) tree and
 * returns a flat array of every non-group (leaf) question, each tagged
 * with its owning section title for display. Used by the reviewer UI,
 * progress calculation, and the assignments editor.
 */
export function getAllLeafQuestions(questionnaire) {
  if (!questionnaire || !Array.isArray(questionnaire.sections)) return [];
  const out = [];
  const walk = (nodes, section) => {
    for (const q of nodes || []) {
      if (q.type === 'group') {
        walk(q.sub_questions, section);
      } else {
        out.push({ ...q, _section: section.title });
      }
    }
  };
  questionnaire.sections.forEach((section) => walk(section.questions, section));
  return out;
}

/** Convenience: just the ids. Handy for default-all-leaves assignments. */
export function getAllLeafQuestionIds(questionnaire) {
  return getAllLeafQuestions(questionnaire).map((q) => q.id);
}

/** True if the given response map has a non-empty answer for questionId. */
export function isQuestionAnswered(questionId, responses) {
  const response = (responses || {})[questionId];
  if (response === undefined || response === null) return false;
  if (Array.isArray(response)) return response.length > 0;
  return String(response).trim() !== '';
}

/**
 * Progress calculation for a (questionnaire, responses) pair. Denominator
 * is leaf questions that pass the visibility DSL; numerator is those that
 * additionally have a non-empty answer.
 */
export function calculateQuestionnaireProgress(questionnaire, responses) {
  const leaves = getAllLeafQuestions(questionnaire);
  const visible = leaves.filter((q) => isVisible(q.visibleIf, responses));
  const answered = visible.filter((q) => isQuestionAnswered(q.id, responses));
  const total = visible.length;
  const count = answered.length;
  return {
    answered: count,
    total,
    percentage: total > 0 ? (count / total) * 100 : 0,
  };
}

/**
 * Transforms a Firestore questionnaire (`{ sections: [{ title, questions }] }`)
 * into the legacy `{ questionnaire: [{ section_title, questions }] }` shape
 * QuestionForm was originally written against. This keeps the refactor
 * surgical — QuestionForm only has to change its visibility and progress
 * calls, not its whole traversal structure.
 */
export function toLegacyShape(questionnaire) {
  if (!questionnaire || !Array.isArray(questionnaire.sections)) {
    return { questionnaire: [] };
  }
  return {
    questionnaire: questionnaire.sections.map((section) => ({
      section_title: section.title,
      section_description: section.description,
      questions: section.questions || [],
    })),
  };
}

/**
 * Compat wrapper: takes a question object from the loaded questionnaire
 * and the current responses, returns whether it should be visible. Calls
 * the DSL evaluator under the hood. Same signature as the legacy
 * isQuestionVisible so QuestionForm's call sites don't have to change.
 */
export function isQuestionVisibleDsl(question, responses) {
  if (!question) return true;
  return isVisible(question.visibleIf, responses);
}

